import {
  GetHomepageHeroById,
  GetHomepageIntroBenefitsById,
  GetHomepageAudienceCulturalById,
  GetHomepageProductMotionById,
  GetHomepageCarouselsById,
  GetHomepageSlidesCarouselById,
} from "@queries/archetypes/GetHomepageById";
import { GetProductPageById } from "@queries/archetypes/GetProductPageById";
import { GetEditorialPageById } from "@queries/archetypes/GetEditorialPageById";
import { GetContentHubPageById } from "@queries/archetypes/GetContentHubPageById";
import { GetConversionPageById } from "@queries/archetypes/GetConversionPageById";
import { GetFaqPageById } from "@queries/archetypes/GetFaqPageById";
import { GetTermsPageById } from "@queries/archetypes/GetTermsPageById";
import { GetCreativeSpecsPageById } from "@queries/archetypes/GetCreativeSpecsPageById";
import { useParams } from "react-router";
import { Forward } from "./Forward";
import { Loading } from "./Loading";
import { NotFound } from "./NotFound";
import { Page } from "./Page";
import { useAsyncAbortable } from "@react-hookz/web";
import { contentfulClient } from "@utils/contentfulClient";
import { useCallback, useEffect } from "react";
import { ArchetypeData, PageInfoType, PageModel } from "../../types/PageModel";
import { useGlobalContext } from "@utils/GlobalContext";
import { isSlugAllowedForLocale } from "@utils/localeRestrictions";

const archetypeQueries = {
  ProductPage: GetProductPageById,
  EditorialPage: GetEditorialPageById,
  ContentHubPage: GetContentHubPageById,
  ConversionPage: GetConversionPageById,
  FaqPage: GetFaqPageById,
  TermsPage: GetTermsPageById,
  CreativeSpecsPage: GetCreativeSpecsPageById,
} as const;

type SimpleArchetypeTypeName = keyof typeof archetypeQueries;
type ArchetypeTypeName = SimpleArchetypeTypeName | "Homepage";

const archetypeDataKeys: Record<SimpleArchetypeTypeName, string> = {
  ProductPage: "productPage",
  EditorialPage: "editorialPage",
  ContentHubPage: "contentHubPage",
  ConversionPage: "conversionPage",
  FaqPage: "faqPage",
  TermsPage: "termsPage",
  CreativeSpecsPage: "creativeSpecsPage",
};

type SlugMapEntry = {
  __typename: ArchetypeTypeName;
  id: string;
};

async function fetchSlugEntry(slug: string): Promise<SlugMapEntry | null> {
  const response = await fetch("/api/content/pages");
  if (!response.ok) return null;
  const slugMap = (await response.json()) as Record<string, SlugMapEntry>;
  return slugMap[slug] ?? null;
}

function extractPageInfo(archetype: ArchetypeData): PageInfoType {
  if (archetype.__typename === "Homepage") {
    const { hero } = archetype.data;
    return {
      __typename: hero.__typename,
      slug: hero.slug,
      title: hero.title,
      theme: hero.theme,
      metadata: hero.metadata,
      markets: hero.markets,
      navigationAccessibilityLabel: hero.navigationAccessibilityLabel,
    };
  }
  const { data } = archetype;
  return {
    __typename: data.__typename,
    slug: data.slug,
    title: "title" in data ? (data.title as string | null) : null,
    theme: "theme" in data ? data.theme : null,
    metadata: data.metadata,
    markets: "markets" in data ? data.markets : null,
    navigationAccessibilityLabel: "navigationAccessibilityLabel" in data ? data.navigationAccessibilityLabel : null,
  };
}

export const PageResolver = () => {
  const { slug = "homepage" } = useParams();
  const { locale, previewContent } = useGlobalContext();

  if (!isSlugAllowedForLocale(slug ?? "", locale)) {
    return <Forward replace to="/" />;
  }

  const getPageData = useCallback(async (): Promise<
    PageModel | { notFound: true }
  > => {
    // Step 1: Look up slug in the server-side slug map
    const slugEntry = await fetchSlugEntry(slug?.toLowerCase());
    if (!slugEntry) return { notFound: true };

    const { __typename: archetypeType, id: archetypeId } = slugEntry;
    const queryVars = { id: archetypeId, locale, preview: previewContent };

    // Step 2: Fetch the full archetype data (includes page info fields)
    let archetype: ArchetypeData;

    if (archetypeType === "Homepage") {
      // Homepage is split into parallel queries to stay under Contentful's 8KB limit
      const [
        heroRes,
        introBenefitsRes,
        audienceCulturalRes,
        productMotionRes,
        carouselsRes,
        slidesCarouselRes,
      ] = await Promise.all([
        contentfulClient.query(GetHomepageHeroById, queryVars),
        contentfulClient.query(GetHomepageIntroBenefitsById, queryVars),
        contentfulClient.query(GetHomepageAudienceCulturalById, queryVars),
        contentfulClient.query(GetHomepageProductMotionById, queryVars),
        contentfulClient.query(GetHomepageCarouselsById, queryVars),
        contentfulClient.query(GetHomepageSlidesCarouselById, queryVars),
      ]);

      const hero = heroRes?.data?.homepage;
      const introBenefits = introBenefitsRes?.data?.homepage;
      const audienceCultural = audienceCulturalRes?.data?.homepage;
      const productMotion = productMotionRes?.data?.homepage;
      const carousels = carouselsRes?.data?.homepage;
      const slidesCarousel = slidesCarouselRes?.data?.homepage;

      if (!hero) return { notFound: true };

      archetype = {
        __typename: "Homepage",
        data: {
          hero,
          introBenefits: introBenefits ?? { introBenefits: null },
          audienceCultural: audienceCultural ?? { audienceCultural: null },
          productMotion: productMotion ?? { productMotion: null },
          carousels: carousels ?? {
            statisticsCarousel: null,
            tabbedCarousel: null,
          },
          slidesCarousel: slidesCarousel ?? { slidesCarousel: null },
        },
      } as ArchetypeData;
    } else {
      const query = archetypeQueries[archetypeType];
      const response = await contentfulClient.query(query, queryVars);
      const dataKey = archetypeDataKeys[archetypeType];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data = (response?.data as any)?.[dataKey];

      if (!data) return { notFound: true };

      archetype = {
        __typename: archetypeType,
        data,
      } as ArchetypeData;
    }

    const pageInfo = extractPageInfo(archetype);
    return { pageInfo, archetype };
  }, [slug, locale, previewContent]);

  const [pageData, actions] = useAsyncAbortable(getPageData);
  useEffect(() => {
    actions.execute();
    return () => actions.abort();
  }, [getPageData, actions]);

  if (pageData.status === "loading" || !pageData.result) {
    return <Loading />;
  }

  if ("notFound" in pageData.result) {
    return <NotFound />;
  }

  return <Page key={slug} page={pageData.result} />;
};
